import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Breadcrumbs,
  alpha,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import PaymentIcon from '@mui/icons-material/Payment';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { authService, UniqCampLogo } from '@uniqcamp/shared';

const DRAWER_WIDTH = 280;

const navItems = [
  { path: '/', label: 'Dashboard', icon: <DashboardIcon /> },
  { path: '/schools', label: 'Schools', icon: <SchoolIcon /> },
  { path: '/users', label: 'Users', icon: <PeopleIcon /> },
  { path: '/payments', label: 'Payments', icon: <PaymentIcon /> },
  { path: '/logs', label: 'Logs', icon: <ListAltIcon /> },
];

const pathToLabel: Record<string, string> = {
  '/': 'Dashboard',
  '/schools': 'Schools',
  '/users': 'Users',
  '/payments': 'Payments',
  '/logs': 'Logs',
};

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = authService.getStoredUser();

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumbLabels = pathSegments.length
    ? [{ label: 'Dashboard', path: '/' }, ...pathSegments.map((segment, i) => ({ label: pathToLabel['/' + pathSegments.slice(0, i + 1).join('/')] || segment, path: '/' + pathSegments.slice(0, i + 1).join('/') }))]
    : [{ label: 'Dashboard', path: '/' }];

  const drawer = (
    <Box sx={{ py: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 0.5 }}>
          <UniqCampLogo style={{ maxHeight: 40, width: 'auto' }} />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Super Admin
        </Typography>
      </Box>
      <List sx={{ flex: 1, px: 1.5 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isActive}
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  py: 1.25,
                  ...(isActive && {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                    color: 'primary.main',
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                  }),
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: isActive ? 600 : 500 }} />
              </ListItemButton>
            </ListItem>
          );
        })}
        <ListItem disablePadding sx={{ mt: 1 }}>
          <ListItemButton onClick={handleLogout} sx={{ py: 1.25 }} color="inherit">
            <ListItemIcon sx={{ minWidth: 40 }}><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
      {user && (
        <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" fontWeight={500}>{String((user as { name?: string }).name ?? '')}</Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: DRAWER_WIDTH },
          color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: { xs: 56, sm: 64 } }}>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 0, display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="body2" sx={{  color: 'inherit' }}>
            {String((user as { name?: string } | null)?.name ?? '')}
          </Typography>
          <Box sx={{ flex: 1 }} />
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: 0 }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              mt: 0,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        <Box sx={{ flex: 1, py: 3, px: { xs: 2, sm: 3 }, width: '100%' }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
              {breadcrumbLabels.map((b, i) =>
                i < breadcrumbLabels.length - 1 ? (
                  <Typography key={b.path} component="button" variant="body2" color="inherit" onClick={() => navigate(b.path)} sx={{ border: 0, background: 'none', cursor: 'pointer', p: 0, '&:hover': { textDecoration: 'underline' } }}>
                    {b.label}
                  </Typography>
                ) : (
                  <Typography key={b.path} variant="body2" color="text.primary" fontWeight={600}>
                    {b.label}
                  </Typography>
                )
              )}
            </Breadcrumbs>
            <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
